import AdyenCheckout from '@adyen/adyen-web';
// eslint-disable-next-line import/no-unresolved
import UIElement from '@adyen/adyen-web/dist/types/components/UIElement';
// eslint-disable-next-line import/no-unresolved
import { PaymentAction } from '@adyen/adyen-web/dist/types/types';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { captureException } from '../../../../services/sentryService';
import { Maybe } from '../../../../types';
import { AdyenConfig } from '../../Adyen.types';
import { handleQrCodeFailureRedirect } from '../utils/redirect';

interface ActionCreatorConfig extends AdyenConfig {
  locale: string;
}
type PaymentDetailsState = { data?: { details?: { payload: string } } };

const translations = (locale: string) => {
  return {
    [locale]: {},
  };
};

export const ACTION_CREATOR_CONTAINER = 'action_creator_container';

const MAX_RETRIES = 2;
const RETRY_WAIT = 2000;
const QR_CODE_SCAN_TIMEOUT = 1000 * 60 * 15;

const useActionCreator = (paymentAction: PaymentAction, config: ActionCreatorConfig) => {
  const { adyenEnvironment, locale, clientKey } = config;

  const [detailsPayload, setDetailPayload] = useState<Maybe<string>>();
  const [, setActionTimeoutId] = useState<NodeJS.Timeout>();
  const [retryCount, setRetryCount] = useState(0);
  const [isActionTimedOut, setActionTimedOut] = useState<boolean>(false);

  const onAdditionalDetails = useCallback((state: PaymentDetailsState) => {
    setActionTimeoutId((timerId) => {
      clearTimeout(timerId);
      return undefined;
    });
    setDetailPayload(state?.data?.details?.payload);
  }, []);

  const paymentActionMemo = useMemo(
    () => paymentAction,
    [paymentAction?.type, paymentAction?.paymentMethodType, paymentAction?.url],
  );

  useEffect(() => {
    let action: Maybe<UIElement>;
    let retryTimeoutId: Maybe<NodeJS.Timeout>, actionTimeOutId: Maybe<NodeJS.Timeout>;
    async function initialize() {
      try {
        const adyen = await AdyenCheckout({
          environment: adyenEnvironment,
          translations: translations(locale),
          locale,
          clientKey,
          onAdditionalDetails,
        });
        if (!adyen) {
          throw new Error('could not create Adyen action object authorization component');
        }
        action = adyen.createFromAction(paymentActionMemo).mount(`#${ACTION_CREATOR_CONTAINER}`);
        actionTimeOutId = setTimeout(() => setActionTimedOut(true), QR_CODE_SCAN_TIMEOUT);
        setActionTimeoutId(actionTimeOutId);
      } catch (error) {
        captureException({ error: error as Error });
        retryTimeoutId = setTimeout(() => {
          setRetryCount((count) => count + 1);
        }, RETRY_WAIT);
      }
    }

    if (retryCount < MAX_RETRIES) {
      initialize();
    } else {
      handleQrCodeFailureRedirect();
    }

    return () => {
      action?.unmount().remove();
      clearTimeout(retryTimeoutId);
      clearTimeout(actionTimeOutId);
    };
  }, [adyenEnvironment, locale, clientKey, paymentActionMemo, onAdditionalDetails, retryCount]);

  return { detailsPayload, isActionTimedOut };
};

export default useActionCreator;
